import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import UpsellTrigger from '@alltrails/amplitude/enums/UpsellTrigger';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useUser from '@alltrails/context/hooks/useUser';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Dialog from '@alltrails/denali/components/Dialog';
import Link from '@alltrails/denali/components/Link';
import Dandelion from '@alltrails/denali/illustrations/Dandelion';
import useReduxListItems from '@alltrails/lists/hooks/useReduxListItems';
import useTrailCompleted from '@alltrails/lists/hooks/useTrailCompleted';
import type { TrailId } from '@alltrails/types/TrailId';
import styles from './UgcActions.module.scss';

const suggestSuccessDialogDurationMs = 3000;

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const SuggestTrailUpdateModal = dynamic(() => import('../SuggestTrailUpdateModal/SuggestTrailUpdateModal'), { ssr: false });
const VerifiedCompletedDialog = dynamic(() => import('../VerifiedCompletedDialog/VerifiedCompletedDialog'), { ssr: false });

const messages = defineMessages({
  MARK_COMPLETED: { defaultMessage: 'Mark as completed' },
  REMOVE_COMPLETED: { defaultMessage: 'Remove from completed' },
  SUGGEST_EDIT: { defaultMessage: 'Suggest trail update' },
  VERIFIED: { defaultMessage: 'Verified completed' },
  THANKS_FOR_SHARING: { defaultMessage: 'Thanks for sharing' },
  FEEDBACK_MSG: { defaultMessage: 'Your feedback makes AllTrails better' }
});

type UgcActionsProps = {
  className?: string;
  slug: string;
  trailId: TrailId;
};

const UgcActions = ({ className, slug, trailId }: UgcActionsProps) => {
  const {
    formattedDefaultMessages: { MARK_COMPLETED, REMOVE_COMPLETED, SUGGEST_EDIT, VERIFIED, THANKS_FOR_SHARING, FEEDBACK_MSG }
  } = useFormatMessage(messages);
  const languageRegionCode = useLanguageRegionCode();
  const user = useUser();
  const { isVerified } = useReduxListItems();
  const verified = isVerified('trail', trailId);
  const { completed, toggleTrailCompleted } = useTrailCompleted(trailId);
  const [signupModalState, setSignupModalState] = useState<
    { isOpen: false } | { isOpen: true; trigger: CarouselDisplayTrigger; upsellTrigger: UpsellTrigger }
  >({ isOpen: false });
  const [isVerifiedDialogOpen, setIsVerifiedDialogOpen] = useState(false);
  const [isSuggestUpdateModalOpen, setIsSuggestUpdateModalOpen] = useState(false);
  const [showSuggestSuccess, setShowSuggestSuccess] = useState(false);

  const onMarkCompletedClick = useCallback(() => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: completed ? 'remove from completed' : 'add to completed',
      trail_page_location: 'reviews',
      trail_page_type: 'trail detail page'
    });

    if (user) {
      toggleTrailCompleted();
    } else {
      setSignupModalState({
        isOpen: true,
        trigger: 'Add Trail to Completed List',
        upsellTrigger: 'Add Trail to Completed List'
      });
    }
  }, [completed, toggleTrailCompleted, trailId, user]);

  const onSuggestEditClick = useCallback(() => {
    if (!user) {
      setSignupModalState({ isOpen: true, trigger: 'Suggest Trail Edit', upsellTrigger: 'Suggest Trail Edit' });
      return;
    }

    setIsSuggestUpdateModalOpen(true);
  }, [user]);

  const onSuggestSuccess = useCallback(() => {
    setIsSuggestUpdateModalOpen(false);
    setShowSuggestSuccess(true);
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (showSuggestSuccess) {
      timeout = setTimeout(() => {
        setShowSuggestSuccess(false);
      }, suggestSuccessDialogDurationMs);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [showSuggestSuccess]);

  return (
    <div className={classNames(styles.container, className)}>
      <Button onClick={onSuggestEditClick} text={SUGGEST_EDIT} variant="default" width="full" />
      <div className={styles.completedContainer}>
        {verified ? (
          <Link
            className={styles.action}
            onClick={() => {
              logTrailDetailsActionTapped({
                trail_id: trailId.toString(),
                trail_page_action: 'add to completed',
                trail_page_location: 'reviews',
                trail_page_type: 'trail detail page'
              });
              setIsVerifiedDialogOpen(true);
            }}
          >
            {VERIFIED}
          </Link>
        ) : (
          <Link className={styles.action} onClick={onMarkCompletedClick}>
            {completed ? REMOVE_COMPLETED : MARK_COMPLETED}
          </Link>
        )}
      </div>
      {isVerifiedDialogOpen && <VerifiedCompletedDialog onClose={() => setIsVerifiedDialogOpen(false)} />}
      {isSuggestUpdateModalOpen && (
        <SuggestTrailUpdateModal
          isOpen={isSuggestUpdateModalOpen}
          onClose={() => setIsSuggestUpdateModalOpen(false)}
          onSuggestSuccess={onSuggestSuccess}
          slug={slug}
          trailId={trailId}
        />
      )}
      {showSuggestSuccess && (
        <Dialog
          body={FEEDBACK_MSG}
          className={styles.suggestSuccess}
          icon={<Dandelion />}
          onRequestClose={() => setShowSuggestSuccess(false)}
          size="md"
          title={THANKS_FOR_SHARING}
        />
      )}
      {signupModalState.isOpen && (
        <SignUpModal
          closeModal={() => setSignupModalState({ isOpen: false })}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          isOpen={signupModalState.isOpen}
          onSuccess={() => {
            setSignupModalState({ isOpen: false });
            if (signupModalState.trigger === 'Add Trail to Completed List') {
              toggleTrailCompleted();
            } else {
              window.open(wrapUrlSafe(`/trail/${slug}/edit?ref=trail-more-edit`, languageRegionCode), '_blank');
            }
          }}
          trigger={signupModalState.trigger}
          upsellTrigger={signupModalState.upsellTrigger}
        />
      )}
    </div>
  );
};

export default UgcActions;
